import type { DesktopStoryCraftFeedback } from "./storyCraftFeedback";
import { averageStoryCraftScore } from "./storyCraftFeedback";
import { CRAFT_AREA_LABELS, STORY_CRAFT_AREA_IDS, type StoryCraftAreaId } from "./craftAreas";

/** Compact Story Craft block for assistant chat system context. */
export function formatStoryCraftForChat(
  feedback: DesktopStoryCraftFeedback | null | undefined,
  options?: { maxChecklist?: number }
): string {
  if (!feedback) return "";
  if (feedback.summary === "Analyzing…" || feedback.summary.startsWith("Analysis failed")) {
    return "";
  }

  const avg = averageStoryCraftScore(feedback);
  const lines: string[] = [
    "## Story Craft analysis (current chapter)",
    avg != null ? `Average grade: ${avg.toFixed(1)}/5` : "Average grade: n/a",
  ];

  const assessment = feedback.assessment;
  for (const key of STORY_CRAFT_AREA_IDS) {
    const row = assessment[key as StoryCraftAreaId];
    if (!row) continue;
    const notes = row.notes?.trim() ? ` — ${row.notes.trim().slice(0, 220)}` : "";
    lines.push(`- ${CRAFT_AREA_LABELS[key]}: ${row.score}/5${notes}`);
  }

  if (feedback.summary?.trim()) {
    lines.push("", `Summary: ${feedback.summary.trim().slice(0, 800)}`);
  }

  const openItems = (feedback.checklist ?? [])
    .filter((c) => !c.isCompleted && c.suggestion.trim())
    .slice(0, options?.maxChecklist ?? 6);
  if (openItems.length > 0) {
    lines.push("", "Open checklist items:");
    for (const item of openItems) {
      lines.push(`- [${item.category}] ${item.suggestion.trim().slice(0, 200)}`);
    }
  }

  lines.push(
    "",
    "When advising on craft, respect the author's voice and story goals. Do not push generic max-conflict or max-pacing; help them improve while preserving intentional quiet, atmosphere, and character marination when that is what they want."
  );

  return lines.join("\n");
}

export function formatWritingResourcesForChat(
  hits: Array<{
    resourceTitle: string;
    itemTitle?: string;
    url: string;
    excerpt?: string;
    craftAreas?: string[];
  }>
): string {
  if (!hits.length) return "";
  const lines = [
    "## Writing resources (cite with links when recommending further learning)",
  ];
  for (const hit of hits.slice(0, 6)) {
    const areas =
      hit.craftAreas && hit.craftAreas.length > 0 ? ` [${hit.craftAreas.join(", ")}]` : "";
    lines.push(`- ${hit.resourceTitle}${areas}: ${hit.url}`);
    if (hit.excerpt?.trim()) {
      lines.push(`  Excerpt: ${hit.excerpt.trim().slice(0, 280)}`);
    }
  }
  return lines.join("\n");
}
